import Analytics, { AnalyticsInstance, AnalyticsPlugin } from 'analytics';

type AdobeAppMeasurementT = {
  useBeacon?: boolean;
};

type AnalyticsWindowT = {
  analytics: AnalyticsInstance;
  analyticsDataLayer: Record<string, unknown>;
  s?: AdobeAppMeasurementT;
} & Window;

type ConfigT = { enabled: boolean; };

type PayloadT = {
  event: string;
  properties: Record<string, unknown>;
  traits: Record<string, unknown>;
};

type EventCallbackT = {
  instance: AnalyticsInstance;
  payload: PayloadT;
};

declare let window: AnalyticsWindowT;

const adobeLaunchURL: string = import.meta.env.VITE_ADOBE_LAUNCH_URL || '';
const isDeployed = import.meta.env.VITE_ENV === 'production' || import.meta.env.VITE_ENV === 'staging';

function buildAdobePlugin(config: ConfigT): AnalyticsPlugin {
  const enabled = config.enabled && !!adobeLaunchURL;

  if (config.enabled && !adobeLaunchURL) {
    console.warn('Adobe not configured');
  }

  return {
    config: { enabled },

    identify: <T extends EventCallbackT>({ payload }: T) => {
      if (payload.traits) {
        window.analyticsDataLayer = payload.traits;
      } else if (!window.analyticsDataLayer) {
        window.analyticsDataLayer = {};
      }
    },

    initialize: () => {
      const adobeLaunch = document.createElement('script');

      adobeLaunch.setAttribute('src', adobeLaunchURL);
      document.head.prepend(adobeLaunch);

      // Silence error message about hotjar not being initialized since not used in staging
      window.addEventListener('error', (e) => {
        if (e.message?.includes('hj is not defined')) e.preventDefault();
      });
    },

    loaded: () => !!window.s,

    name: 'adobe',

    track: <T extends EventCallbackT>({ payload, instance }: T) => {
      const user = instance.user();

      window.analyticsDataLayer = user?.traits ?? window.analyticsDataLayer ?? {};

      // Send this hit via navigator.sendBeacon so it is queued 'fire and forget'
      // and still completes if the page unloads mid-request. AppMeasurement
      // resets useBeacon to false after each hit, so it must be set before
      // every track call.
      if (window.s) {
        window.s.useBeacon = true;
      }

      document.body.dispatchEvent(
        new CustomEvent(payload.event, {
          bubbles: true,
          composed: true,
          detail: payload.properties,
        }),
      );
    },
  };
}

export const analytics = Analytics({
  app: 'account-home-frontend',
  debug: !isDeployed,
  plugins: [buildAdobePlugin({ enabled: isDeployed })],
}) as AnalyticsInstance;

window.analytics = analytics;
